import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import {
  apiUTCToLocalDateTime,
  useDebounce,
  usePrevious,
} from "@jugl-web/utils";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { ChatMessageBubbleAction } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageBubbleAction";
import { InstagramMessageType, useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import isSameDay from "date-fns/isSameDay";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { InstagramMessageBubble } from "../InstagramMessageBubble";
import { CRMInfoMessage } from "../CRMInfoMessage";

export const InstagramMessageItem: FC<{
  message: InstagramMessageModel;
  onShowOnScreen?: (message: InstagramMessageModel) => void;
  onReplyPreviewClick?: (replyInfo: ChatMessageReplyInfo) => void;
  isHighlighted?: boolean;
  messages: InstagramMessageModel[];
}> = ({
  message,
  onShowOnScreen,
  onReplyPreviewClick,
  isHighlighted,
  messages,
}) => {
  const { inView, ref } = useInView();
  const debouncedInView = useDebounce(inView, 100);
  const previousInView = usePrevious(debouncedInView);
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const meId = useSelector(selectUserId);

  const [sendInstagramMessageReaction] =
    integrationsApi.useSendInstagramMessageReactionMutation();

  const {
    setReplyMessage,
    isChatExpired: isMessageActionsDisabled,
    selectedConversation,
  } = useClientSupportPageContext();

  useEffect(() => {
    if (debouncedInView === previousInView) {
      return;
    }
    if (debouncedInView) {
      onShowOnScreen?.(message);
    }
  }, [debouncedInView, onShowOnScreen, message, previousInView]);

  const groupInfo = useMemo(() => {
    const idx = messages.findIndex((item) => item.msg_id === message.msg_id);

    const result = {
      isFirst: false,
      isLast: false,
    };
    const prevMessage = messages[idx - 1];
    const nextMessage = messages[idx + 1];
    if (
      !prevMessage ||
      prevMessage?.from !== message.from ||
      prevMessage?.type === "info" ||
      prevMessage?.auto_response !== message.auto_response ||
      !isSameDay(
        apiUTCToLocalDateTime(prevMessage.inserted_at),
        apiUTCToLocalDateTime(message.inserted_at)
      )
    ) {
      result.isFirst = true;
    }
    if (
      !nextMessage ||
      nextMessage?.from !== message.from ||
      nextMessage?.type === "info" ||
      nextMessage.auto_response !== message.auto_response
    ) {
      result.isLast = true;
    }

    return result;
  }, [message, messages]);

  const handleTriggerAction = useCallback(
    (
      action: ChatMessageBubbleAction,
      triggeredMessage: InstagramMessageModel
    ) => {
      if (selectedConversation && action === ChatMessageBubbleAction.REPLY) {
        setReplyMessage(selectedConversation.conv_id, triggeredMessage);
      }
    },
    [selectedConversation, setReplyMessage]
  );

  const handleReaction = useCallback(
    (reaction: string) => {
      if (selectedConversation && message.msg_id) {
        sendInstagramMessageReaction({
          entityId,
          data: {
            payload: {
              recipient: {
                id: selectedConversation.conv_id,
              },
              sender_action:
                message.payload.reaction &&
                message.payload.reaction.reaction === reaction &&
                message.payload.reaction.user_id === meId
                  ? "unreact"
                  : "react",
              payload: {
                message_id: message.msg_id,
                reaction,
              },
            },
          },
        });
      }
    },
    [
      selectedConversation,
      message.msg_id,
      message.payload.reaction,
      sendInstagramMessageReaction,
      entityId,
      meId,
    ]
  );

  const $content = useMemo(() => {
    if (
      message.type === "info" &&
      "msg_type" in message &&
      message.msg_type === InstagramMessageType.text
    ) {
      return (
        <CRMInfoMessage
          message={message.payload.message.text}
          timestamp={message.inserted_at}
        />
      );
    }

    return (
      <InstagramMessageBubble
        message={message}
        isHighlighted={isHighlighted}
        groupInfo={groupInfo}
        onReplyPreviewClick={onReplyPreviewClick}
        onTriggerAction={handleTriggerAction}
        onReaction={handleReaction}
        isMessageActionsDisabled={isMessageActionsDisabled}
      />
    );
  }, [
    groupInfo,
    handleReaction,
    handleTriggerAction,
    isHighlighted,
    isMessageActionsDisabled,
    message,
    onReplyPreviewClick,
  ]);

  return <div ref={ref}>{$content}</div>;
};
